import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { fetchData } from "../../api";

export default function ServiceDetail() {
    const { slug } = useParams();
    const [service, setService] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);

    useEffect(() => {
        const fetchService = async () => {
            try {
                setLoading(true);
                const services = await fetchData("servicetwos", true);
                const foundService = services.find(s => s.Slug === slug);
                
                if (foundService) {
                    setService(foundService);
                } else {
                    setError("Service niet gevonden");
                }
            } catch (err) {
                setError("Fout bij het laden van de service");
                console.error("Error fetching service:", err);
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchService();
        }
    }, [slug]);

    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && isImageModalOpen) {
                closeImageModal();
            }
        };

        if (isImageModalOpen) {
            document.addEventListener('keydown', handleEscKey);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
            document.body.style.overflow = 'unset';
        };
    }, [isImageModalOpen]);

    const openImageModal = (image) => {
        setSelectedImage(image);
        setIsImageModalOpen(true);
        setZoomLevel(1);
    };

    const closeImageModal = () => {
        setIsImageModalOpen(false);
        setSelectedImage(null);
        setZoomLevel(1);
    };

    const zoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.25, 2));
    };

    const zoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
    };

    const resetZoom = () => {
        setZoomLevel(1);
    };

    const handleImageModalClick = (e) => {
        if (e.target === e.currentTarget) {
            closeImageModal();
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#286766]"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">{error}</h1>
                    <a href="/" className="text-[#286766] hover:text-[#27352C] transition-colors">
                        Terug naar home
                    </a>
                </div>
            </div>
        );
    }

    if (!service) {
        return null;
    }

    const renderRichText = (content) => {
        if (!content) return null;
        
        if (typeof content === 'string') {
            return <div dangerouslySetInnerHTML={{ __html: content }} />;
        }
        
        if (Array.isArray(content)) {
            return content.map((block, index) => {
                switch (block.type) {
                    case 'paragraph':
                        return (
                            <p key={index} className="mb-4 text-gray-700 leading-relaxed">
                                {block.children?.map(child => child.text).join('')}
                            </p>
                        );
                    case 'heading':
                        const HeadingTag = `h${block.level}`;
                        return (
                            <HeadingTag key={index} className={`font-bold text-gray-900 mb-4 ${
                                block.level === 1 ? 'text-3xl' :
                                block.level === 2 ? 'text-2xl' :
                                block.level === 3 ? 'text-xl' : 'text-lg'
                            }`}>
                                {block.children?.map(child => child.text).join('')}
                            </HeadingTag>
                        );
                    default:
                        return null;
                }
            });
        }
        
        return null;
    };

    return (
        <div className="min-h-screen bg-white">
            <main className="relative before:bg-[#000]/50 before:backdrop-blur-[0px] before:absolute before:left-0 before:top-0 before:w-full before:h-full max-sm:h-[360px]">
                <img
                    src={service.HeroImage 
                        ? `${process.env.NEXT_PUBLIC_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl"}${service.HeroImage.url}`
                        : `${process.env.NEXT_PUBLIC_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl"}${service.Icon?.url}`}
                    alt={service.Title}
                    className="w-full h-full object-cover lg:max-h-[800px]"
                />
                
                <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 z-20 text-center max-w-4xl w-full px-4">
                    {/* Breadcrumb - Hidden on mobile */}
                    <nav className="text-sm text-white/80 mb-4 hidden sm:block">
                        <a href="/" className="hover:text-white transition-colors">Home</a>
                        <span className="mx-2">/</span>
                        <a href="/#services" className="hover:text-white transition-colors">Services</a>
                        <span className="mx-2">/</span>
                        <span className="text-white">{service.Title}</span>
                    </nav>
                    
                    {/* Icon - Smaller on mobile */}
                    <div className="mb-4 sm:mb-6">
                        <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-white/20 backdrop-blur-sm rounded-2xl shadow-lg">
                            <img 
                                src={`${process.env.NEXT_PUBLIC_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl"}${service.Icon?.url}`} 
                                alt={service.Title}
                                className="h-8 w-8 sm:h-12 sm:w-12 object-contain"
                            />
                        </div>
                    </div>
                    
                    {/* Title - More responsive sizing */}
                    <h1 className="text-3xl sm:text-5xl lg:text-[80px] font-semibold text-white capitalize mb-3 sm:mb-6 leading-tight">
                        {service.Title}
                    </h1>
                    
                    {/* Description - Shorter on mobile */}
                    <p className="text-white mb-6 sm:mb-8 max-w-2xl mx-auto text-sm sm:text-lg line-clamp-3 sm:line-clamp-none">
                        {service.Description}
                    </p>
                    
                    {/* CTA Button - Smaller on mobile */}
                    <a 
                        href="/contact" 
                        className="relative bg-[#27352C] w-[160px] h-[48px] sm:w-[200px] sm:h-[58px] text-white px-3 flex mx-auto items-center justify-center rounded-[0_0_24px_0] hover:bg-[#1e2920] transition-colors text-sm sm:text-base"
                    >
                        Vraag offerte aan
                    </a>
                </div>
            </main>

            {service.DetailedContent && (
                <section className="py-16 px-4">
                    <div className="container mx-auto max-w-4xl">
                        <div className="prose prose-lg max-w-none text-gray-700">
                            {renderRichText(service.DetailedContent)}
                        </div>
                    </div>
                </section>
            )}

            {service.Features && Array.isArray(service.Features) && service.Features.length > 0 && (
                <section className="py-16 px-4 bg-gray-50">
                    <div className="container mx-auto max-w-6xl">
                        <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
                            Waarom kiezen voor onze {service.Title.toLowerCase()}?
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {service.Features.map((feature, index) => (
                                <div key={index} className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                                    <div className="w-12 h-12 bg-gradient-to-br from-[#286766] to-[#27352C] rounded-xl flex items-center justify-center mb-4">
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-semibold mb-3 text-gray-900">{feature.title}</h3>
                                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {service.Gallery && service.Gallery.length > 0 && (
                <section className="py-16 px-4">
                    <div className="container mx-auto max-w-6xl">
                        <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
                            Voorbeelden van ons werk
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {service.Gallery.map((image, index) => (
                                <div 
                                    key={index} 
                                    className="group relative rounded-xl overflow-hidden shadow-lg cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl"
                                    onClick={() => openImageModal(image.url)}
                                >
                                    <img 
                                        src={`${process.env.NEXT_PUBLIC_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl"}${image.url}`}
                                        alt={`${service.Title} voorbeeld ${index + 1}`}
                                        className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full p-3">
                                            <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="py-16 px-4 bg-gradient-to-br from-[#286766] to-[#27352C]">
                <div className="container mx-auto max-w-4xl text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                        Klaar om te beginnen met uw project?
                    </h2>
                    <p className="text-xl text-white/90 mb-8">
                        Neem contact met ons op voor een vrijblijvende offerte en persoonlijk advies.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a 
                            href="/contact" 
                            className="bg-white text-[#286766] px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105"
                        >
                            Vraag offerte aan
                        </a>
                        <a 
                            href="tel:+31242403447" 
                            className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-[#286766] transition-all duration-300"
                        >
                            Bel direct: +31 (0)24 2403 447
                        </a>
                    </div>
                </div>
            </section>

            {/* Image Modal with Zoom */}
            {isImageModalOpen && selectedImage && (
                <div 
                    className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={handleImageModalClick}
                >
                    <div className="relative w-full h-full flex items-center justify-center">
                        <button
                            onClick={closeImageModal}
                            className="absolute top-4 right-4 z-10 bg-black/50 text-white rounded-full p-3 hover:bg-black/70 transition-colors duration-200"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="absolute top-4 left-4 z-10 flex gap-2">
                            <button
                                onClick={zoomOut}
                                className="bg-black/50 text-white rounded-full p-3 hover:bg-black/70 transition-colors duration-200"
                                disabled={zoomLevel <= 0.5}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                </svg>
                            </button>
                            <button
                                onClick={zoomIn}
                                className="bg-black/50 text-white rounded-full p-3 hover:bg-black/70 transition-colors duration-200"
                                disabled={zoomLevel >= 2}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                            </button>
                            <button
                                onClick={resetZoom}
                                className="bg-black/50 text-white rounded-full p-3 hover:bg-black/70 transition-colors duration-200"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                </svg>
                            </button>
                        </div>

                        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                            {Math.round(zoomLevel * 100)}%
                        </div>

                        <div className="w-full h-full flex items-center justify-center overflow-auto">
                            <img
                                src={`${process.env.NEXT_PUBLIC_STRAPI_URL || "https://api.bouwbedrijfgeluk.nl"}${selectedImage}`}
                                alt="Zoomed Image"
                                className="max-w-full max-h-full object-contain transition-transform duration-300"
                                style={{ transform: `scale(${zoomLevel})` }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
} 